/**
 * TabRenderer - Renders tab characters with proper spacing based on tabstop positions
 * 
 * This service handles the visual rendering of tab characters (.lp-tab elements) 
 * within paragraphs, calculating their widths based on:
 * 1. The horizontal position of the tab within the line
 * 2. The tabstop definitions from the paragraph's data-tabstops attribute
 * 3. The default tab interval (36 points = 0.5 inches) when no tabstops are defined
 * 
 * Tab width calculation follows Word's behavior:
 * - Find the tab's current horizontal position within the paragraph
 * - Find the next tabstop position that is greater than the current position
 * - Set the tab width to reach that tabstop position
 * - If no tabstop is found, use the default interval (round up to next multiple of 36pt)
 * 
 * @requires TabstopService
 * @see plans/lp-001-tabs/tabstop-contract.md
 */
angular.module('LeasePilot').factory('TabRenderer', ['TabstopService', '$timeout', function(TabstopService, $timeout) {
  
  // Default tab interval in points (0.5 inches)
  const DEFAULT_TAB_INTERVAL = 36;
  
  // Conversion factor: 1 point = 1.333333 pixels (at 96 DPI)
  const POINTS_TO_PIXELS = 96 / 72;
  
  // Minimum tab width in points to ensure visibility
  const MIN_TAB_WIDTH = 4;

  // Tolerance for matching tabstop positions (in points)
  const TABSTOP_MATCH_TOLERANCE = 0.5;

  // Zero-width space marker for tab placeholder content.
  const TAB_MARKER_TEXT = '\u200B';

  // Runtime debug switch (in browser console):
  // window.__LP_TAB_DEBUG = true
  // window.__LP_TAB_DEBUG = { enabled: true, pid: '2200' }
  function getDebugConfig() {
    if (typeof window === 'undefined') {
      return { enabled: false };
    }
    const cfg = window.__LP_TAB_DEBUG;
    if (cfg === true) {
      return { enabled: true };
    }
    if (cfg && typeof cfg === 'object') {
      return {
        enabled: cfg.enabled !== false,
        pid: cfg.pid !== undefined && cfg.pid !== null ? String(cfg.pid) : null
      };
    }
    return { enabled: false };
  }

  function shouldDebugParagraph(paragraph) {
    const cfg = getDebugConfig();
    if (!cfg.enabled) {
      return false;
    }
    if (!cfg.pid) {
      return true;
    }
    const paragraphPid = paragraph && paragraph.getAttribute ? paragraph.getAttribute('pid') : null;
    return paragraphPid === cfg.pid;
  }

  function debugTab(paragraph, stage, payload) {
    if (!shouldDebugParagraph(paragraph)) {
      return;
    }
    const paragraphPid = paragraph && paragraph.getAttribute ? paragraph.getAttribute('pid') : null;
    console.debug('[TabRenderer]', Object.assign({ stage: stage, pid: paragraphPid }, payload || {}));
  }

  /**
   * Gets the horizontal offset of an element relative to the document content start
   * @param {HTMLElement} element - The element to measure
   * @param {HTMLElement} paragraph - The paragraph element containing the tab
   * @returns {number} - Offset in pixels from the document content start (after document padding)
   * 
   * Note: Tabstop positions are measured from document content start (after document padding).
   * This matches how the ruler calculates positions when adding tabstops.
   * We need to measure relative to the document container, not the paragraph, because
   * paragraphs can have margin-left which shifts them, but tabstop positions are absolute.
   */
  function getHorizontalOffset(element, paragraph) {
    if (!element || !paragraph) {
      return 0;
    }

    // Find the document container (same as ruler uses)
    const documentContainer = document.querySelector('.lease.lease--editor');
    if (!documentContainer) {
      // Fallback: measure relative to paragraph if document container not found
      const elementRect = element.getBoundingClientRect();
      const paragraphRect = paragraph.getBoundingClientRect();
      const paragraphStyle = window.getComputedStyle(paragraph);
      const paddingLeft = parseFloat(paragraphStyle.paddingLeft) || 0;
      return Math.max(0, elementRect.left - paragraphRect.left - paddingLeft);
    }

    // Get the bounding rectangles (these return post-transform visual pixels)
    const elementRect = element.getBoundingClientRect();
    const documentRect = documentContainer.getBoundingClientRect();

    // Detect CSS transform scale on the document container.
    // Use computed style width (float) for pre-transform layout pixels to avoid
    // integer rounding drift from offsetWidth under transform: scale(...).
    // Fallback to offsetWidth when computed width is unavailable.
    const computedWidth = parseFloat(window.getComputedStyle(documentContainer).width) || 0;
    const baseWidth = computedWidth || documentContainer.offsetWidth || 0;
    const scale = baseWidth > 0 ? (documentRect.width / baseWidth) : 1;

    // Get document container's border and padding (pre-transform CSS pixels)
    const documentStyle = window.getComputedStyle(documentContainer);
    const borderLeftWidth = parseFloat(documentStyle.borderLeftWidth) || 0;
    const documentPaddingLeft = parseFloat(documentStyle.paddingLeft) || 0;

    // Convert the visual-pixel distance to CSS pixels, then subtract border + padding
    // to get the offset from the content edge (matching the tabstop coordinate system)
    const offset = (elementRect.left - documentRect.left) / scale - borderLeftWidth - documentPaddingLeft;

    return Math.max(0, offset);
  }

  /**
   * Measures the insertion position of a tab without its current width affecting line wrapping.
   * This prevents a feedback loop where tab width changes alter measured X, causing oscillation.
   * @param {HTMLElement} tabElement - The tab element to measure
   * @param {HTMLElement} paragraph - The paragraph containing the tab
   * @returns {number} - Offset in pixels from document content start
   */
  function getTabInsertionOffset(tabElement, paragraph) {
    if (!tabElement || !paragraph) {
      return 0;
    }

    // Primary strategy: measure a temporary zero-width marker inserted at the
    // tab insertion point. This is more stable than measuring the tab box itself
    // and avoids Range/caret quirks in contenteditable inline layout.
    let marker = null;
    let markerParent = null;
    try {
      marker = document.createElement('span');
      marker.setAttribute('aria-hidden', 'true');
      marker.style.display = 'inline-block';
      marker.style.width = '0';
      marker.style.minWidth = '0';
      marker.style.padding = '0';
      marker.style.margin = '0';
      marker.style.border = '0';
      marker.style.overflow = 'hidden';
      marker.style.fontSize = '0';
      marker.style.lineHeight = '0';
      marker.style.verticalAlign = 'baseline';

      markerParent = tabElement.parentNode;
      if (!markerParent) {
        throw new Error('Tab has no parent node');
      }

      markerParent.insertBefore(marker, tabElement);
      const markerOffset = getHorizontalOffset(marker, paragraph);
      return markerOffset;
    } catch (e) {
      // Fall back to tab-box measurement below.
    } finally {
      if (marker && markerParent && marker.parentNode === markerParent) {
        markerParent.removeChild(marker);
      }
    }

    const originalWidth = tabElement.style.width;
    const originalMinWidth = tabElement.style.minWidth;

    try {
      // Temporarily remove tab width from layout so measurement is based on
      // insertion point (caret position), not current rendered tab width.
      tabElement.style.width = '0pt';
      tabElement.style.minWidth = '0pt';
      return getHorizontalOffset(tabElement, paragraph);
    } finally {
      tabElement.style.width = originalWidth;
      tabElement.style.minWidth = originalMinWidth;
    }
  }

  /**
   * Parses existing round-trip tabstop metadata from a tab element.
   * @param {HTMLElement} tabElement - The tab element
   * @returns {{ position: number, alignment: string }|null} - Parsed metadata, or null if missing/invalid
   */
  function getExistingAwTabstopInfo(tabElement) {
    if (!tabElement || !tabElement.style) {
      return null;
    }

    const awPosRaw = tabElement.style.getPropertyValue('-aw-tabstop-pos');
    const awAlignRaw = (tabElement.style.getPropertyValue('-aw-tabstop-align') || '').trim().toLowerCase();
    const awPos = parseFloat(awPosRaw);

    if (isNaN(awPos) || awPos < 0) {
      return null;
    }

    const validAlignment = ['left', 'right', 'center', 'decimal'].includes(awAlignRaw)
      ? awAlignRaw
      : 'left';

    return {
      position: awPos,
      alignment: validAlignment
    };
  }

  /**
   * Converts pixels to points
   * @param {number} pixels - Value in pixels
   * @returns {number} - Value in points
   */
  function pixelsToPoints(pixels) {
    return pixels / POINTS_TO_PIXELS;
  }

  /**
   * Converts points to pixels
   * @param {number} points - Value in points
   * @returns {number} - Value in pixels
   */
  function pointsToPixels(points) {
    return points * POINTS_TO_PIXELS;
  }

  /**
   * Finds the text segment after a tab that should be used for alignment.
   *
   * For center/right/decimal alignment, Word only aligns text up to the NEXT TAB
   * or (if no next tab) the first word/phrase that fits. Text beyond that flows
   * normally from the tabstop position.
   *
   * @param {HTMLElement} tabElement - The tab element
   * @param {HTMLElement} paragraph - The paragraph containing the tab
   * @returns {string} - Text up to next tab, or first word if no next tab
   */
  function getTextAfterTab(tabElement, paragraph) {
    if (!tabElement || !paragraph) {
      return '';
    }

    // Find next tab in paragraph (if any)
    var allTabs = Array.from(paragraph.querySelectorAll('.' + TabstopService.TAB_CLASS));
    var tabIndex = allTabs.indexOf(tabElement);
    var nextTab = (tabIndex >= 0 && tabIndex < allTabs.length - 1)
      ? allTabs[tabIndex + 1]
      : null;

    try {
      var text = '';
      var node = tabElement.nextSibling;

      // Collect text from siblings
      while (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Stop at next tab
          if (node.classList && node.classList.contains(TabstopService.TAB_CLASS)) {
            break;
          }
          // Stop at line breaks
          if (node.tagName === 'BR' || node.tagName === 'HARD-RETURN') {
            break;
          }
          // Include inline element text
          text += node.textContent || '';
        }
        node = node.nextSibling;
      }

      // If there was a next tab, we have the exact text segment - use it all
      if (nextTab) {
        return text.trim();
      }

      // No next tab - Word behavior: only align the first "word" or short phrase
      // Find the first space after some content (at least 1 char)
      text = text.trimStart();
      var firstSpace = text.indexOf(' ');

      if (firstSpace > 0) {
        // Return just the first word
        text = text.substring(0, firstSpace);
      } else if (text.length > 50) {
        // No space found but text is long - truncate
        text = text.substring(0, 50);
      }

      return text;
    } catch (e) {
      return '';
    }
  }

  /**
   * Measures the rendered width of text after a tab element.
   *
   * For center/right/decimal alignment, only measures:
   * 1. Text up to the next tab (if present)
   * 2. Or: the first word (matching Word's behavior)
   *
   * @param {HTMLElement} tabElement - The tab element
   * @param {HTMLElement} paragraph - The paragraph containing the tab
   * @returns {number} - The width in points
   */
  function measureTextAfterTab(tabElement, paragraph) {
    if (!tabElement || !paragraph) {
      return 0;
    }

    // Use getTextAfterTab to get the correct text segment (first word or until next tab)
    var textToMeasure = getTextAfterTab(tabElement, paragraph);

    if (!textToMeasure) {
      return 0;
    }

    try {
      // Create a span for measurement with paragraph styling
      var measureSpan = document.createElement('span');
      var paragraphStyle = window.getComputedStyle(paragraph);

      measureSpan.style.fontFamily = paragraphStyle.fontFamily;
      measureSpan.style.fontSize = paragraphStyle.fontSize;
      measureSpan.style.fontWeight = paragraphStyle.fontWeight;
      measureSpan.style.fontStyle = paragraphStyle.fontStyle;
      measureSpan.style.letterSpacing = paragraphStyle.letterSpacing;
      measureSpan.style.visibility = 'hidden';
      measureSpan.style.position = 'absolute';
      measureSpan.style.whiteSpace = 'nowrap';
      measureSpan.style.left = '-9999px';
      measureSpan.style.top = '-9999px';
      measureSpan.textContent = textToMeasure;

      document.body.appendChild(measureSpan);
      var widthPixels = measureSpan.getBoundingClientRect().width;
      document.body.removeChild(measureSpan);

      return pixelsToPoints(widthPixels);
    } catch (e) {
      return 0;
    }
  }

  /**
   * Finds the position of the decimal point in text
   * @param {string} text - The text to search
   * @returns {number} - Index of decimal point, or text length if not found
   */
  function findDecimalPosition(text) {
    if (!text) {
      return 0;
    }
    var decimalIndex = text.indexOf('.');
    return decimalIndex >= 0 ? decimalIndex : text.length;
  }

  /**
   * Measures the width of text up to the decimal point
   * @param {HTMLElement} tabElement - The tab element
   * @param {HTMLElement} paragraph - The paragraph containing the tab
   * @returns {number} - The width in points up to the decimal point
   */
  function measureWidthToDecimal(tabElement, paragraph) {
    if (!tabElement || !paragraph) {
      return 0;
    }

    var textAfter = getTextAfterTab(tabElement, paragraph);
    var decimalPos = findDecimalPosition(textAfter);
    var textBeforeDecimal = textAfter.substring(0, decimalPos);

    if (!textBeforeDecimal) {
      return 0;
    }

    // For decimal alignment, we need to measure the text before the decimal
    // We'll use the paragraph's computed style as an approximation
    try {
      var measureSpan = document.createElement('span');
      var paragraphStyle = window.getComputedStyle(paragraph);

      measureSpan.style.fontFamily = paragraphStyle.fontFamily;
      measureSpan.style.fontSize = paragraphStyle.fontSize;
      measureSpan.style.fontWeight = paragraphStyle.fontWeight;
      measureSpan.style.fontStyle = paragraphStyle.fontStyle;
      measureSpan.style.letterSpacing = paragraphStyle.letterSpacing;
      measureSpan.style.visibility = 'hidden';
      measureSpan.style.position = 'absolute';
      measureSpan.style.whiteSpace = 'pre';
      measureSpan.style.left = '-9999px';
      measureSpan.style.top = '-9999px';
      measureSpan.textContent = textBeforeDecimal;

      document.body.appendChild(measureSpan);
      var widthPixels = measureSpan.getBoundingClientRect().width;
      document.body.removeChild(measureSpan);

      return pixelsToPoints(widthPixels);
    } catch (e) {
      return 0;
    }
  }

  /**
   * Calculates the width needed for a tab to reach the next tabstop
   * Handles all alignment types: left, center, right, decimal
   * @param {number} currentPosition - Current position in points
   * @param {Array} tabstops - Array of tabstop objects
   * @param {HTMLElement} tabElement - The tab element (needed for non-left alignment)
   * @param {HTMLElement} paragraph - The paragraph element (needed for non-left alignment)
   * @param {{ position: number, alignment: string, leader?: string }|null} preferredTabstop - Optional preferred tabstop target (e.g., imported -aw metadata)
   * @returns {Object} - { width: number (points), tabstop: object|null, leader: string }
   */
  function calculateTabWidth(currentPosition, tabstops, tabElement, paragraph, preferredTabstop) {
    if (!isFinite(currentPosition)) {
      currentPosition = 0;
    }

    // Prefer an explicit imported target when valid and still ahead of current position.
    // If current position has already passed it, fall back to normal "next tabstop" behavior.
    var nextTabstop = null;
    if (preferredTabstop && preferredTabstop.position > currentPosition + TABSTOP_MATCH_TOLERANCE) {
      nextTabstop = preferredTabstop;
    } else {
      // Find the next tabstop beyond current position
      nextTabstop = TabstopService.findNextTabstop(tabstops, currentPosition);
    }

    if (nextTabstop) {
      var baseWidth = nextTabstop.position - currentPosition;
      var adjustedWidth = baseWidth;
      var alignment = nextTabstop.alignment || 'left';

      // Handle non-left alignments by adjusting tab width based on text after tab
      // Per Word's behavior: if text is too long, tab shrinks to MIN_TAB_WIDTH (text may overflow)
      if (alignment !== 'left' && tabElement && paragraph) {
        var textWidth = 0;

        switch (alignment) {
          case 'center':
            // Center: text midpoint aligns at tabstop
            // Tab width = base - (textWidth / 2)
            textWidth = measureTextAfterTab(tabElement, paragraph);
            adjustedWidth = baseWidth - (textWidth / 2);
            break;

          case 'right':
            // Right: text end aligns at tabstop
            // Tab width = base - textWidth
            textWidth = measureTextAfterTab(tabElement, paragraph);
            adjustedWidth = baseWidth - textWidth;
            break;

          case 'decimal':
            // Decimal: decimal point aligns at tabstop
            // Tab width = base - widthToDecimal
            var widthToDecimal = measureWidthToDecimal(tabElement, paragraph);
            adjustedWidth = baseWidth - widthToDecimal;
            break;
        }
      }

      return {
        width: Math.max(MIN_TAB_WIDTH, adjustedWidth),
        tabstop: nextTabstop,
        leader: nextTabstop.leader || 'none'
      };
    }

    // No explicit tabstop found - use default interval
    var nextDefaultPosition = Math.ceil((currentPosition + 1) / DEFAULT_TAB_INTERVAL) * DEFAULT_TAB_INTERVAL;
    var width = Math.max(MIN_TAB_WIDTH, nextDefaultPosition - currentPosition);

    return {
      width: width,
      tabstop: null,
      leader: 'none'
    };
  }

  /**
   * Measures rendered tab end position for a candidate width.
   * Returns null when measurement fails.
   * @param {HTMLElement} tabElement
   * @param {HTMLElement} paragraph
   * @param {number} width
   * @returns {number|null}
   */
  function measureTabEndForWidth(tabElement, paragraph, width) {
    if (!tabElement || !paragraph || !isFinite(width)) {
      return null;
    }

    const originalWidth = tabElement.style.width;
    const originalMinWidth = tabElement.style.minWidth;

    try {
      tabElement.style.width = width + 'pt';
      tabElement.style.minWidth = width + 'pt';

      const insertionPixels = getTabInsertionOffset(tabElement, paragraph);
      const insertionPoints = isFinite(insertionPixels) ? pixelsToPoints(insertionPixels) : NaN;
      if (!isFinite(insertionPoints)) {
        return null;
      }
      return insertionPoints + width;
    } finally {
      tabElement.style.width = originalWidth;
      tabElement.style.minWidth = originalMinWidth;
    }
  }

  /**
   * Chooses the width that minimizes actual rendered tab-end error under the
   * current layout (including justify feedback).
   *
   * Uses coarse then fine search around the best candidate.
   *
   * @param {HTMLElement} tabElement - The tab element
   * @param {HTMLElement} paragraph - The containing paragraph
   * @param {number} targetTabstopPosition - Desired tab end in points
   * @param {number} initialWidth - Starting width estimate in points
   * @returns {number} - Best width in points
   */
  function stabilizeWidthForJustifiedLayout(tabElement, paragraph, targetTabstopPosition, initialWidth) {
    if (!tabElement || !paragraph || !isFinite(targetTabstopPosition)) {
      return initialWidth;
    }

    const baseWidth = Math.max(MIN_TAB_WIDTH, isFinite(initialWidth) ? initialWidth : MIN_TAB_WIDTH);
    let bestWidth = baseWidth;
    let bestError = Infinity;
    let bestEnd = null;

    function considerWidth(width, phase) {
      if (!isFinite(width)) {
        return;
      }
      const normalizedWidth = Math.max(MIN_TAB_WIDTH, width);
      const measuredEnd = measureTabEndForWidth(tabElement, paragraph, normalizedWidth);
      if (!isFinite(measuredEnd)) {
        return;
      }
      const error = Math.abs(measuredEnd - targetTabstopPosition);
      const isBetter = error < bestError || (Math.abs(error - bestError) < 1e-6 && Math.abs(normalizedWidth - baseWidth) < Math.abs(bestWidth - baseWidth));
      if (isBetter) {
        bestWidth = normalizedWidth;
        bestError = error;
        bestEnd = measuredEnd;
      }
      debugTab(paragraph, 'search-sample', {
        phase: phase,
        candidateWidth: normalizedWidth,
        measuredEnd: measuredEnd,
        targetTabstopPosition: targetTabstopPosition,
        error: error
      });
    }

    // Seed candidates
    considerWidth(baseWidth, 'seed');
    considerWidth(MIN_TAB_WIDTH, 'seed');
    considerWidth(baseWidth + 12, 'seed');
    considerWidth(baseWidth + 24, 'seed');

    // Coarse search around the initial width
    const coarseStart = Math.max(MIN_TAB_WIDTH, baseWidth - 30);
    const coarseEnd = baseWidth + 30;
    for (let width = coarseStart; width <= coarseEnd; width += 1) {
      considerWidth(width, 'coarse');
    }

    // Fine search around coarse best
    const fineStart = Math.max(MIN_TAB_WIDTH, bestWidth - 2);
    const fineEnd = bestWidth + 2;
    for (let width = fineStart; width <= fineEnd + 1e-9; width += 0.1) {
      considerWidth(width, 'fine');
    }

    debugTab(paragraph, 'search-best', {
      baseWidth: baseWidth,
      bestWidth: bestWidth,
      bestEnd: bestEnd,
      targetTabstopPosition: targetTabstopPosition,
      bestError: bestError
    });

    return bestWidth;
  }

  /**
   * Applies width, leader, and Aspose round-trip styling to a tab element.
   * Sets -aw-tabstop-pos and -aw-tabstop-align so HTML2DocX can use them when converting back to Word.
   * @param {HTMLElement} tabElement - The tab span element
   * @param {number} widthInPoints - Width to apply in points
   * @param {string} leader - Leader style ('none', 'dots', 'dashes', 'underline')
   * @param {{ position: number, alignment: string }|null} tabstopInfo - Tabstop position (pt) and alignment for -aw-tabstop-*; null to clear
   */
  function applyTabStyle(tabElement, widthInPoints, leader, tabstopInfo) {
    if (!tabElement) {
      return;
    }

    // Set width in points (CSS will handle the conversion)
    tabElement.style.width = widthInPoints + 'pt';
    tabElement.style.minWidth = widthInPoints + 'pt';

    // -aw-tabstop-pos and -aw-tabstop-align for Word round-trip (used by HTML2DocX)
    if (tabstopInfo && typeof tabstopInfo.position === 'number' && tabstopInfo.alignment) {
      tabElement.style.setProperty('-aw-tabstop-pos', tabstopInfo.position + 'pt');
      tabElement.style.setProperty('-aw-tabstop-align', tabstopInfo.alignment);
    } else {
      tabElement.style.removeProperty('-aw-tabstop-pos');
      tabElement.style.removeProperty('-aw-tabstop-align');
    }

    // Remove any existing leader classes
    tabElement.classList.remove(
      'lp-tab--leader-dots',
      'lp-tab--leader-dashes',
      'lp-tab--leader-underline'
    );

    // Apply leader class if applicable
    if (leader && leader !== 'none') {
      tabElement.classList.add('lp-tab--leader-' + leader);
    }
  }

  /**
   * Ensures a tab element has the correct attributes
   * @param {HTMLElement} tabElement - The tab span element
   */
  function ensureTabAttributes(tabElement) {
    if (!tabElement) {
      return;
    }

    // Ensure contenteditable="false"
    if (tabElement.getAttribute('contenteditable') !== 'false') {
      tabElement.setAttribute('contenteditable', 'false');
    }

    // Ensure the element has content so selection/deletion logic can still
    // target the tab span, but without adding visible width. Normalize any
    // whitespace-only or &nbsp; content to the zero-width marker.
    const currentText = tabElement.textContent || '';
    const normalized = currentText.replace(/\u00A0/g, '').trim();
    if (!currentText || normalized === '') {
      tabElement.textContent = TAB_MARKER_TEXT;
    }
  }

  /**
   * Renders a single tab element within its paragraph
   * @param {HTMLElement} tabElement - The tab span element to render
   * @returns {boolean} - True if rendering was successful
   */
  function renderTab(tabElement) {
    if (!tabElement || !TabstopService.isTabElement(tabElement)) {
      return false;
    }

    // Ensure proper attributes
    ensureTabAttributes(tabElement);

    // Find the containing paragraph
    const paragraph = TabstopService.findParagraphElement(tabElement);
    if (!paragraph) {
      // No paragraph found - use default width; no tabstop info
      applyTabStyle(tabElement, DEFAULT_TAB_INTERVAL, 'none', null);
      return true;
    }

    // Get tabstops for this paragraph
    const tabstops = TabstopService.parseTabstopsFromElement(paragraph);

    const hasWidth = tabElement.style.width && tabElement.style.width !== '';

    // Measure insertion position with tab width temporarily removed to avoid
    // oscillation from width->wrap->position feedback on repeated re-renders.
    const positionPixels = getTabInsertionOffset(tabElement, paragraph);
    const positionPoints = isFinite(positionPixels) ? pixelsToPoints(positionPixels) : 0;

    // Decide whether existing -aw metadata should remain authoritative for this tab.
    // Prefer existing -aw metadata when it still matches a paragraph tabstop
    // (or when paragraph has no explicit tabstops), so passive re-renders
    // don't rewrite round-trip metadata.
    const existingAwTabstopInfo = getExistingAwTabstopInfo(tabElement);
    const shouldPreserveExistingAw =
      !!existingAwTabstopInfo &&
      tabstops.length > 0 &&
      tabstops.some(function(ts) {
        const tsAlignment = ts.alignment || 'left';
        return (
          Math.abs(ts.position - existingAwTabstopInfo.position) <= TABSTOP_MATCH_TOLERANCE &&
          tsAlignment === existingAwTabstopInfo.alignment
        );
      });

    // Use preserved -aw metadata as preferred visual target for imported tabs.
    // This keeps in-app rendering closer to Word semantics for round-tripped docs.
    const preferredTabstop = shouldPreserveExistingAw
      ? {
          position: existingAwTabstopInfo.position,
          alignment: existingAwTabstopInfo.alignment,
          leader: (tabstops.find(function(ts) {
            const tsAlignment = ts.alignment || 'left';
            return (
              Math.abs(ts.position - existingAwTabstopInfo.position) <= TABSTOP_MATCH_TOLERANCE &&
              tsAlignment === existingAwTabstopInfo.alignment
            );
          }) || {}).leader || 'none'
        }
      : null;

    // Calculate required width (pass tabElement and paragraph for alignment handling)
    const result = calculateTabWidth(positionPoints, tabstops, tabElement, paragraph, preferredTabstop);
    let safeWidth = isFinite(result.width) && result.width > 0 ? result.width : MIN_TAB_WIDTH;

    // In justified paragraphs, solve left-tab widths iteratively in the final
    // layout mode to reduce first-line drift caused by justification feedback.
    const paragraphTextAlign = window.getComputedStyle(paragraph).textAlign;
    const targetAlignment = shouldPreserveExistingAw
      ? existingAwTabstopInfo.alignment
      : ((result.tabstop && result.tabstop.alignment) || 'left');
    const targetTabstopPosition = shouldPreserveExistingAw
      ? existingAwTabstopInfo.position
      : (result.tabstop ? result.tabstop.position : NaN);

    if (
      paragraphTextAlign === 'justify' &&
      targetAlignment === 'left' &&
      isFinite(targetTabstopPosition)
    ) {
      debugTab(paragraph, 'render-before-stabilize', {
        positionPoints: positionPoints,
        initialWidth: safeWidth,
        targetTabstopPosition: targetTabstopPosition,
        paragraphTextAlign: paragraphTextAlign,
        targetAlignment: targetAlignment
      });
      safeWidth = stabilizeWidthForJustifiedLayout(
        tabElement,
        paragraph,
        targetTabstopPosition,
        safeWidth
      );
    }

    // Build computed tabstop info for -aw-tabstop-*.
    const tabEndPosition = positionPoints + safeWidth;
    const computedTabstopInfo = result.tabstop
      ? { position: result.tabstop.position, alignment: result.tabstop.alignment || 'left' }
      : { position: tabEndPosition, alignment: 'left' };
    const tabstopInfo = shouldPreserveExistingAw ? existingAwTabstopInfo : computedTabstopInfo;

    // Apply style when width OR round-trip metadata/leader changed.
    // We still avoid redundant width writes on tiny recalc jitter.
    const currentWidthPt = parseFloat(tabElement.style.width) || 0;
    const widthChanged = !hasWidth || !isFinite(currentWidthPt) || Math.abs(currentWidthPt - safeWidth) > 0.5;

    const currentAwPos = tabElement.style.getPropertyValue('-aw-tabstop-pos');
    const currentAwAlign = tabElement.style.getPropertyValue('-aw-tabstop-align');
    const targetAwPos =
      tabstopInfo && typeof tabstopInfo.position === 'number' ? (tabstopInfo.position + 'pt') : '';
    const targetAwAlign = tabstopInfo && tabstopInfo.alignment ? tabstopInfo.alignment : '';
    const tabstopMetaChanged = currentAwPos !== targetAwPos || currentAwAlign !== targetAwAlign;

    const expectedLeaderClass = result.leader && result.leader !== 'none'
      ? ('lp-tab--leader-' + result.leader)
      : '';
    const hasExpectedLeaderClass = expectedLeaderClass
      ? tabElement.classList.contains(expectedLeaderClass)
      : !tabElement.classList.contains('lp-tab--leader-dots') &&
        !tabElement.classList.contains('lp-tab--leader-dashes') &&
        !tabElement.classList.contains('lp-tab--leader-underline');
    const leaderChanged = !hasExpectedLeaderClass;

    if (widthChanged || tabstopMetaChanged || leaderChanged) {
      applyTabStyle(tabElement, safeWidth, result.leader, tabstopInfo);
    }

    const measuredEndForSafeWidth = measureTabEndForWidth(tabElement, paragraph, safeWidth);

    debugTab(paragraph, 'render-final', {
      positionPoints: positionPoints,
      safeWidth: safeWidth,
      tabEndPosition: tabEndPosition,
      measuredEndForSafeWidth: measuredEndForSafeWidth,
      widthChanged: widthChanged,
      tabstopMetaChanged: tabstopMetaChanged,
      leaderChanged: leaderChanged,
      targetAwPos: targetAwPos,
      targetAwAlign: targetAwAlign
    });

    return true;
  }

  /**
   * Renders all tab elements within a paragraph
   * @param {HTMLElement} paragraph - The paragraph element
   * @returns {number} - Number of tabs rendered
   */
  function renderTabsInParagraph(paragraph) {
    if (!paragraph) {
      return 0;
    }

    const tabElements = paragraph.querySelectorAll('.' + TabstopService.TAB_CLASS);
    let count = 0;

    // Process tabs in order (left to right)
    // Each tab's position depends on the previous tabs' widths
    tabElements.forEach(function(tabElement) {
      if (renderTab(tabElement)) {
        count++;
      }
    });

    return count;
  }

  /**
   * Renders all tab elements within a container (or the entire document)
   * @param {HTMLElement} container - The container element (defaults to document)
   * @returns {number} - Number of tabs rendered
   */
  function renderAllTabs(container) {
    if (!container) {
      container = document;
    }

    // Find all paragraphs with tab elements
    const paragraphs = container.querySelectorAll(
      TabstopService.PARAGRAPH_SELECTOR + ':has(.' + TabstopService.TAB_CLASS + ')'
    );

    let totalCount = 0;

    paragraphs.forEach(function(paragraph) {
      totalCount += renderTabsInParagraph(paragraph);
    });

    // Also find any loose tab elements not in paragraphs
    const looseTabs = TabstopService.findTabElements(container).filter(function(tab) {
      return !TabstopService.findParagraphElement(tab);
    });

    looseTabs.forEach(function(tabElement) {
      if (renderTab(tabElement)) {
        totalCount++;
      }
    });

    return totalCount;
  }

  /**
   * Schedules a deferred render for tabs within a container
   * Useful when DOM may not have settled yet (e.g., after content load)
   * @param {HTMLElement} container - The container element
   * @param {number} delay - Delay in milliseconds (default: 0)
   * @returns {Promise} - Resolves when rendering is complete
   */
  function scheduleRender(container, delay) {
    delay = delay || 0;
    
    return $timeout(function() {
      return renderAllTabs(container);
    }, delay);
  }

  /**
   * Creates a MutationObserver to automatically re-render tabs when content changes
   * @param {HTMLElement} container - The container to observe
   * @param {Object} options - Observer options
   * @returns {MutationObserver} - The observer instance (call disconnect() to stop)
   */
  function observeChanges(container, options) {
    options = options || {};
    const debounceMs = options.debounce || 100;
    
    let debounceTimer = null;
    // Track mouse-down state so we can suppress renders while the user is
    // drag-selecting.  Renders that would fire during a drag are deferred
    // and executed once on mouseup so the layout doesn't jump mid-selection.
    let isMouseDown = false;
    let pendingRenderFn = null;

    container.addEventListener('mousedown', function() {
      isMouseDown = true;
    }, true);

    document.addEventListener('mouseup', function() {
      if (!isMouseDown) {
        return;
      }
      isMouseDown = false;
      if (pendingRenderFn) {
        const fn = pendingRenderFn;
        pendingRenderFn = null;
        setTimeout(fn, debounceMs);
      }
    }, true);

    /**
     * Schedules a render function, deferring it if the user is mid-drag.
     */
    function scheduleRenderFn(fn) {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(function() {
        debounceTimer = null;
        if (isMouseDown) {
          // User is still dragging — stash the render for mouseup
          pendingRenderFn = fn;
          return;
        }
        fn();
      }, debounceMs);
    }

    /**
     * Checks if a node or its parent paragraph contains tabs
     * @param {Node} node - The node to check
     * @returns {HTMLElement|null} - The paragraph containing tabs, or null
     */
    function findParagraphWithTabs(node) {
      if (!node) {
        return null;
      }

      // Find the containing paragraph
      const paragraph = TabstopService.findParagraphElement(node);
      if (!paragraph) {
        return null;
      }

      // Check if paragraph contains tabs
      const hasTabs = paragraph.querySelector && paragraph.querySelector('.' + TabstopService.TAB_CLASS);
      return hasTabs ? paragraph : null;
    }

    /**
     * Checks if a node is a rangy selection boundary marker.
     * Rangy's saveSelection() inserts temporary <span> markers with
     * class "rangySelectionBoundary" that are removed by restoreSelection().
     * These DOM mutations should not trigger tab re-renders.
     */
    function isRangyMarker(node) {
      return node && node.nodeType === Node.ELEMENT_NODE &&
        node.classList && node.classList.contains('rangySelectionBoundary');
    }

    /**
     * Checks if a mutation batch contains only rangy marker operations
     * and text-node splits (from splitBoundaries). These are side-effects
     * of setDecoration/selection inspection and don't change visible content.
     */
    function isRangyOnlyBatch(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];

        if (mutation.type === 'attributes') {
          // Attribute changes (e.g. data-tabstops) are never rangy artifacts
          return false;
        }

        if (mutation.type === 'childList') {
          // Check if all added/removed nodes are rangy markers or text nodes
          // (splitBoundaries creates new text-node siblings)
          var nodes = [];
          mutation.addedNodes.forEach(function(n) { nodes.push(n); });
          mutation.removedNodes.forEach(function(n) { nodes.push(n); });

          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType === Node.TEXT_NODE) {
              // Text node additions from splitBoundaries - benign
              continue;
            }
            if (isRangyMarker(node)) {
              continue;
            }
            // Non-rangy element added/removed - real mutation
            return false;
          }
        }

        if (mutation.type === 'characterData') {
          if (mutation.oldValue === mutation.target.data) {
            // No actual change
            continue;
          }
          // characterData from splitBoundaries: the parent should have the
          // same aggregate text content. Check if sibling text nodes were
          // also added in this batch (indicating a split, not a real edit).
          var parentNode = mutation.target.parentNode;
          if (!parentNode) {
            return false;
          }
          var hasSiblingTextAdded = mutations.some(function(m) {
            if (m.type !== 'childList' || m.target !== parentNode) {
              return false;
            }
            var found = false;
            m.addedNodes.forEach(function(n) {
              if (n.nodeType === Node.TEXT_NODE) {
                found = true;
              }
            });
            return found;
          });
          if (!hasSiblingTextAdded) {
            // Genuine text edit, not a split
            return false;
          }
        }
      }
      return true;
    }

    const observer = new MutationObserver(function(mutations) {
      // Avoid re-rendering while the user is actively selecting text; this reduces
      // visible jumping caused by layout recalculations during drag-selection.
      if (isMouseDown) {
        return;
      }
      if (window && window.getSelection) {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed) {
          return;
        }
      }

      // Skip mutation batches that are purely rangy selection-boundary
      // marker insertions/removals and text-node splits from splitBoundaries.
      // These are triggered by setDecoration() on click/mouseup and don't
      // represent actual content changes that would affect tab layout.
      if (isRangyOnlyBatch(mutations)) {
        return;
      }

      // Track paragraphs that need re-rendering
      const paragraphsToRerender = new Set();
      let shouldRerenderAll = false;

      mutations.forEach(function(mutation) {
        // Check for tab element additions/removals
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (isRangyMarker(node)) {
                return; // Skip rangy markers
              }
              if (TabstopService.isTabElement(node) ||
                  node.querySelector && node.querySelector('.' + TabstopService.TAB_CLASS)) {
                shouldRerenderAll = true;
              }
            }
          });

          mutation.removedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (isRangyMarker(node)) {
                return; // Skip rangy markers
              }
              if (TabstopService.isTabElement(node)) {
                const paragraph = TabstopService.findParagraphElement(mutation.target);
                if (paragraph) {
                  paragraphsToRerender.add(paragraph);
                }
              }
            }
          });
        }

        // Check for tabstop attribute changes
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-tabstops') {
          shouldRerenderAll = true;
        }

        // Check for text content changes (typing) - only re-render if paragraph contains tabs.
        // We explicitly ignore "changes" where the text content is the same as the old value,
        // which covers selection-only updates that some browsers report as characterData mutations.
        if (mutation.type === 'characterData') {
          if (mutation.oldValue === mutation.target.data) {
            return;
          }
          const paragraph = findParagraphWithTabs(mutation.target);
          if (paragraph) {
            paragraphsToRerender.add(paragraph);
          }
        } else if (mutation.type === 'childList' && mutation.target.nodeType === Node.TEXT_NODE) {
          const paragraph = findParagraphWithTabs(mutation.target);
          if (paragraph) {
            paragraphsToRerender.add(paragraph);
          }
        }
      });

      if (shouldRerenderAll) {
        scheduleRenderFn(function() {
          renderAllTabs(container);
        });
      } else if (paragraphsToRerender.size > 0) {
        var paragraphs = Array.from(paragraphsToRerender);
        scheduleRenderFn(function() {
          paragraphs.forEach(function(paragraph) {
            renderTabsInParagraph(paragraph);
          });
        });
      }
    });
    
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tabstops'],
      characterData: true,
      characterDataOldValue: true // Need oldValue to skip selection-only "changes"
    });
    
    return observer;
  }

  // Public API
  return {
    // Constants
    DEFAULT_TAB_INTERVAL: DEFAULT_TAB_INTERVAL,
    POINTS_TO_PIXELS: POINTS_TO_PIXELS,
    MIN_TAB_WIDTH: MIN_TAB_WIDTH,

    // Rendering functions
    renderTab: renderTab,
    renderTabsInParagraph: renderTabsInParagraph,
    renderAllTabs: renderAllTabs,
    scheduleRender: scheduleRender,

    // Tab element functions
    ensureTabAttributes: ensureTabAttributes,
    applyTabStyle: applyTabStyle,

    // Calculation functions
    calculateTabWidth: calculateTabWidth,
    getHorizontalOffset: getHorizontalOffset,
    pixelsToPoints: pixelsToPoints,
    pointsToPixels: pointsToPixels,

    // Alignment helper functions
    getTextAfterTab: getTextAfterTab,
    measureTextAfterTab: measureTextAfterTab,
    measureWidthToDecimal: measureWidthToDecimal,
    findDecimalPosition: findDecimalPosition,

    // Observer for automatic re-rendering
    observeChanges: observeChanges
  };
}]);
